import { getUserErrorMessage } from '@gokrew/ui/utility/utility';
import {
  MyTeamApprovalConflict,
  type MyTeamApprovalRequest,
} from '@gokrew/models/my-team';
import i18next from '@/i18n';

/** Why a request left the inbox without the approver's decision landing. */
export interface ApprovalNotice {
  key: string;
  title: string;
  message: string;
  tone: 'info' | 'warning';
}

/**
 * The approver's explanation for a refused decision, shared by the Approvals
 * tab and the dashboard "To approve" card. The server's message is already
 * translated and, for a request that moved (409 `approvals.ownerChanged`),
 * names who has it now.
 */
export function toApprovalNotice(
  error: unknown,
  request: Pick<MyTeamApprovalRequest, 'id' | 'employeeName'>,
  t: (key: string, options?: Record<string, unknown>) => string
): ApprovalNotice {
  const response = (
    error as { response?: { status?: number; data?: { errorCode?: string } } }
  ).response;
  const code = response?.data?.errorCode;
  const name = request.employeeName;
  const message = response
    ? getUserErrorMessage(error, i18next)
    : t('approvals.decide_failed');
  if (
    code === MyTeamApprovalConflict.OwnerChanged ||
    response?.status === 403
  ) {
    return {
      key: request.id,
      title: t('approvals.moved_title', { name }),
      message,
      tone: 'info',
    };
  }
  if (code === MyTeamApprovalConflict.AlreadyDecided) {
    return {
      key: request.id,
      title: t('approvals.decided_title', { name }),
      message,
      tone: 'info',
    };
  }
  return {
    key: request.id,
    title: t('approvals.failed_title', { name }),
    message,
    tone: 'warning',
  };
}
